import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "../lib/cn";

/**
 * ActionIconTile — the chat-chrome flat action tile (B19 / PQA#2620 round 2):
 * a rounded-SQUARE, hairline-bordered, icon-only control for action rows
 * (the share receipt's copy/open/platform/agent row, the table copy button,
 * block-card actions).
 *
 * A SIBLING of `IconButton` by design, not a variant of it (design pick on
 * the ui-first umbrella): the platform round IconButton (borderless ghost,
 * fixed 40, toolbars/close affordances) and this tile (bordered square tile,
 * 36, chat-surface action rows) change for different reasons, so they are
 * two primitives. IconButton's round/fixed-40 doctrine is untouched.
 *
 * Shape (canonical for the tile family):
 * - 36×36 (`h-9 w-9`), `rounded-lg`, hairline border on the card surface,
 *   muted glyph that inks up on hover with the border strengthening.
 * - `no-underline` is load-bearing on the anchor form: consumers with
 *   Tailwind preflight OFF get the UA anchor underline leaking into content
 *   otherwise (the uni#541 regression class).
 * - Required `aria-label` (TS-enforced, the IconButton precedent). Pair with
 *   the consumer's tooltip (unified's UiTooltip promotes the label to the
 *   accessible name on icon-only controls, so label and tooltip stay one
 *   string).
 * - `asChild` composes onto a real anchor — share targets are links, and the
 *   anchor semantics (middle-click, copy-address) must survive.
 *
 * Copied/pressed feedback stays in the CONSUMER: swap the icon and the
 * aria-label (the share receipt's Copy -> Check swap). Clipboard access and
 * reset timers are behavior, not shape, and do not belong in the tile.
 *
 * Press language, a deliberate stance: NO press-down bounce here, unlike
 * IconButton/Button/KeycapButton. The tile is a fidelity port of the shipped
 * B19 treatment (uni#577), which speaks hover/focus color changes only; the
 * chat-chrome action rows it serves read calmer without the key-press
 * metaphor. If the owner's playground gate wants the press unified across
 * the icon-control family, adopting ICON_PRESS here is a one-line follow-up.
 */
export const actionIconTileClasses = cn(
  "inline-flex h-9 w-9 shrink-0 select-none items-center justify-center rounded-lg",
  "border border-border/70 bg-card text-muted-foreground no-underline",
  "transition-colors hover:border-border hover:text-foreground",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
  "disabled:pointer-events-none disabled:opacity-50",
  "[&_svg]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
);

type TileElementProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">;

export interface ActionIconTileProps extends TileElementProps {
  /** Screen-reader label. Required because the tile has no visible text. */
  "aria-label": string;
  /** Render children via Radix Slot (e.g. a real `<a>` for share targets). */
  asChild?: boolean;
}

export const ActionIconTile = React.forwardRef<HTMLButtonElement, ActionIconTileProps>(
  ({ className, asChild = false, type, "aria-label": ariaLabel, ...props }, ref) => {
    if (process.env.NODE_ENV !== "production" && (!ariaLabel || ariaLabel.trim() === "")) {
      // eslint-disable-next-line no-console
      console.warn(
        "ActionIconTile: missing or empty `aria-label`. Icon-only controls need a descriptive label for screen readers.",
      );
    }
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        // A bare <button> natively submits forms; default it safe. The Slot
        // form leaves the child element's own semantics alone.
        {...(asChild ? {} : { type: type ?? "button" })}
        aria-label={ariaLabel}
        className={cn(actionIconTileClasses, className)}
        {...props}
      />
    );
  },
);
ActionIconTile.displayName = "ActionIconTile";
